import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';

const Home: React.FC = () => {
  const [photos, setPhotos] = useState<Array<{_id: string; imageUrl: string; title: string}>>([]); 

  useEffect(() => {
    fetch(`${API_URL}/api/photos`)
      .then(res => res.json())
      .then(data => setPhotos(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-black">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30 z-10"></div>
        <div className="absolute inset-0">
          <img 
            src="/Image/bg-image.webp" 
            alt="Studio Portrait" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 text-center text-white section-padding animate-fade-in">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-7xl mb-4 md:mb-6 leading-tight px-4">
            Your Best Memory, Our Priority
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-6 md:mb-8 font-light max-w-2xl mx-auto px-4">
            Professional photography services for portraits, weddings, and special moments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:space-x-4 justify-center px-4">
            <Link to="/portfolio" className="btn-primary w-full sm:w-auto text-center">
              View Portfolio
            </Link>
            <Link to="/booking" className="btn-secondary w-full sm:w-auto text-center">
              Book Session
            </Link>
          </div>
        </div>
      </section>

      {/* Photo Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="section-padding">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-4 px-4">Our Work</h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto px-4">
              Explore our diverse portfolio showcasing moments of beauty, emotion, and artistry
            </p>
          </div>
          
          <div className="columns-2 sm:columns-2 lg:columns-3 xl:columns-4 gap-1 space-y-1">
            {[
              "/Our work/458571049_1203474897590904_1178998376117893680_n.jpg",
              "/Our work/465672690_18057600709856022_8794106365032111548_n.jpg",
              "/Our work/527447684_18082995961856022_6703135943638386021_n.jpg",
              "/Our work/537811422_18085102795856022_8732428343338121940_n.jpg",
              "/Our work/542848944_18086360623856022_5341439942931618558_n.jpg",
              "/Our work/568641957_18092094856856022_2893869183946565675_n.jpg",
              "/Our work/611689375_18099185314856022_4818964324351301038_n.jpg",
              "/Our work/615330693_18099699898856022_2263390946159481294_n.jpg"
            ].map((image, index) => (
              <div key={index} className="group cursor-pointer overflow-hidden break-inside-avoid mb-1">
                <img 
                  src={image} 
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-auto transition-all duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-20 bg-gray-50">
        <div className="section-padding">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Photography Services</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Professional photography tailored to capture your most important moments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { 
                image: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80",
                title: "Portrait Sessions",
                description: "Professional headshots and personal portraits"
              },
              { 
                image: "https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80",
                title: "Wedding Photography",
                description: "Complete wedding day coverage and memories"
              },
              { 
                image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80",
                title: "Corporate Events",
                description: "Professional event and business photography"
              }
            ].map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="overflow-hidden mb-4">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-64 sm:h-72 md:h-80 object-cover transition-all duration-500 group-hover:scale-105"
                  />
                </div>
                <h3 className="font-serif text-2xl mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Large Feature Photos */}
      <section className="py-20 bg-white">
        <div className="section-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="space-y-4 md:space-y-8">
              <img 
                src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Studio Work"
                className="w-full h-64 sm:h-80 md:h-96 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Portrait Work"
                className="w-full h-48 sm:h-56 md:h-64 object-cover"
              />
            </div>
            <div className="space-y-4 md:space-y-8 mt-4 md:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Fashion Photography"
                className="w-full h-48 sm:h-56 md:h-64 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1522673607200-164d1b6ce486?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Wedding Photography"
                className="w-full h-64 sm:h-80 md:h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Sliding Gallery Section */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="section-padding">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Recent Highlights</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A continuous showcase of our latest photography work
            </p>
          </div>
          
          <div className="relative">
            <div className="flex animate-slide space-x-6">
              {[...photos, ...photos].map((photo, index) => (
                <div key={index} className="flex-shrink-0">
                  <img 
                    src={photo.imageUrl} 
                    alt={photo.title}
                    className="w-64 sm:w-72 md:w-80 h-80 sm:h-88 md:h-96 object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-padding text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-4 md:mb-6 px-4">Ready to Create Something Beautiful?</h2>
          <p className="text-gray-600 text-base sm:text-lg mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Let's collaborate to bring your vision to life. Book a consultation and discover 
            how we can capture your story with artistic excellence.
          </p>
          <Link to="/booking" className="btn-primary text-base sm:text-lg px-8 sm:px-12 py-3 sm:py-4 mx-4">
            Book Your Session
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;